/**
 * Recognizes creation-SHAPED language anywhere in `text` — a creation verb
 * near a UI/app noun. Deliberately permissive: it says nothing about whether
 * a project is currently open, and it is NOT safe to consult on its own when
 * one is (see `shouldRouteAsAppCreation` — that is the review-round fix for
 * exactly this: a Critical regression where an EDIT message inside an open
 * project, e.g. "이 화면에 버튼 하나 더 만들어줘" ("add one more button to
 * this screen") or "이 페이지 화면을 좀더 예쁘게 만들어줘" ("make this page's
 * screen prettier"), matched this function — same shape as a real creation
 * request — and got rerouted toward "start a new app" instead of applying
 * the edit to the open one).
 *
 * Known, disclosed residual gap (not the mechanism this task-4 review-round
 * fix addresses, and explicitly NOT closed by adding more nouns per that
 * review — "do not ship a longer keyword list"): this still misses 폼,
 * 페이지, and English `form`/`page`/`signup`. A prompt using only those words
 * is under-matched here. That is a precision gap in THIS function, orthogonal
 * to the mechanism fix below (which is about WHEN this function may be
 * trusted, not about making its own word list exhaustive).
 */
export function isAppCreationPrompt(text: string): boolean {
  return (
    /\b(create|build|make|generate)\b.*\b(app|application|website|dashboard|screen)\b/i.test(
      text,
    ) ||
    /\b(app|application|website|dashboard|screen)\b.*\b(create|build|make|generate)\b/i.test(
      text,
    ) ||
    // 화면 ("screen") joins 앱/애플리케이션/웹사이트/대시보드 — a request that
    // only names a screen ("문의 화면 하나를 만들어줘") is still a creation
    // request; the noun list existed to rule out an edit/mutation message,
    // not to require the word "app" specifically. Measured gap: Studio
    // slice-1 step ① ('이름, 이메일, 메시지를 입력받고 제출 버튼이 있는 문의
    // 화면 하나를 만들어줘') fell through this check to the edit/target-select
    // branch because it never says 앱/애플리케이션/웹사이트/대시보드.
    /(앱|애플리케이션|웹사이트|대시보드|화면).*(만들|생성|구축)|(만들|생성|구축).*(앱|애플리케이션|웹사이트|대시보드|화면)/i.test(
      text,
    )
  )
}

/**
 * A NARROW, high-precision signal that the human explicitly wants a
 * NEW/DIFFERENT app or project, distinct from `isAppCreationPrompt`'s broad
 * "creation-shaped language anywhere" match. Requires an explicit
 * new/another marker (새/새로운/다른/new/another) next to the noun, not just
 * any creation verb — an edit message ("이 화면 …을 만들어줘") never says
 * "새"/"다른"/"new"/"another", so it correctly does not match here.
 *
 * This function is deliberately conservative in the OTHER direction from
 * `isAppCreationPrompt`: under-matching here is safe (it falls back to
 * "edit the open project", the correct default when a project is open and
 * intent is ambiguous), where under-matching in `isAppCreationPrompt` would
 * be unsafe if it were the only signal consulted with no project open (there
 * would be nothing to fall back to). That asymmetry — not a longer keyword
 * list — is the actual fix; see `shouldRouteAsAppCreation`.
 */
export function isExplicitNewAppSignal(text: string): boolean {
  return (
    /\b(?:a\s+|start\s+a\s+)?new\s+(?:app|application|website|dashboard|project)\b/i.test(
      text,
    ) ||
    /\banother\s+(?:app|application|website|dashboard|project)\b/i.test(text) ||
    /(?:새로운|새)\s*(?:앱|애플리케이션|웹사이트|대시보드|프로젝트)/.test(text) ||
    /다른\s*(?:앱|애플리케이션|웹사이트|대시보드|프로젝트)\s*(?:을|를)?\s*(?:만들|생성|시작)/.test(
      text,
    ) ||
    /새로\s*(?:시작|만들)/.test(text)
  )
}

/**
 * Whether a submitted message should be routed as an app-CREATION request
 * rather than an edit/mutation of whatever project is currently open.
 *
 * The mechanism fix (review round, task 4): context decides which classifier
 * applies, not a longer word list on one classifier. When a project is open,
 * the default is EDIT — `isAppCreationPrompt` (a permissive "creation-shaped
 * language anywhere" match) is not consulted at all, because its own noun
 * list ("화면"/"screen" among them) matches ordinary edit language just as
 * often as real creation requests once there is an open project's content to
 * refer to. Only an EXPLICIT new/another-app signal (`isExplicitNewAppSignal`)
 * overrides the edit default. When no project is open there is nothing to
 * default to "edit" of, so the broad `isAppCreationPrompt` is the right (and
 * only reachable) classifier — this is the path Studio slice-1 step ①
 * ('이름, 이메일, 메시지를 입력받고 제출 버튼이 있는 문의 화면 하나를
 * 만들어줘', submitted with no project open) goes through.
 */
export function shouldRouteAsAppCreation(
  text: string,
  hasOpenProject: boolean,
): boolean {
  return hasOpenProject
    ? isExplicitNewAppSignal(text)
    : isAppCreationPrompt(text)
}
